import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCategories } from '../context/CategoryContext';
import { UPLOAD_URL } from '../config/config';

const CategoriesSection = () => {
  const [hoveredItem, setHoveredItem] = useState(null);
  const { categories, loading, error } = useCategories();
  const navigate = useNavigate();
  const location = useLocation();

  const handleMouseEnter = (itemId) => setHoveredItem(itemId);
  const handleMouseLeave = () => setHoveredItem(null);

  const handleKeyDown = (event, itemId) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleCategoryClick(categories.find(cat => cat.id === itemId));
    }
  };

  const handleCategoryClick = (category) => {
    if (!category) return;

    // Navigate to products page with category parameter
    const searchParams = new URLSearchParams();
    searchParams.set('category', category.name);

    const newUrl = `/products?${searchParams.toString()}`;

    // If already on products page, force reload by navigating away and back
    if (location.pathname === '/products') {
      navigate('/');
      setTimeout(() => {
        navigate(newUrl);
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100);
      }, 50);
    } else {
      navigate(newUrl);
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
  };

  const responsiveClasses = [
    'relative row-[1_/_3] max-lg:col-[1_/_3] max-lg:h-[350px] max-lg:row-[1] max-md:col-[1_/_3] max-md:h-[400px] max-md:row-[1] max-sm:col-[1] max-sm:h-[350px] max-sm:row-[1]',
    'relative col-[2_/_4] row-[1] size-full max-lg:col-[1_/_3] max-lg:h-[250px] max-lg:row-[2] max-md:col-[1_/_3] max-md:h-[250px] max-md:row-[2] max-sm:col-[1] max-sm:h-[300px] max-sm:row-[2]',
    'relative col-[2] row-[2] size-full max-lg:col-[1] max-lg:h-[200px] max-lg:row-[3] max-md:col-[1] max-md:h-[200px] max-md:row-[3] max-sm:col-[1] max-sm:h-[250px] max-sm:row-[3]',
    'relative col-[3] row-[2] size-full max-lg:col-[2] max-lg:h-[200px] max-lg:row-[3] max-md:col-[2] max-md:h-[200px] max-md:row-[3] max-sm:col-[1] max-sm:h-[250px] max-sm:row-[4]',
    'relative col-[4] row-[1_/_3] size-full max-lg:col-[1_/_3] max-lg:h-[300px] max-lg:row-[4] max-md:col-[1_/_3] max-md:h-[300px] max-md:row-[4] max-sm:col-[1] max-sm:h-[350px] max-sm:row-[5]'
  ];

  return (
    <section
      className="flex flex-col justify-center items-start px-2 py-8 mx-auto my-0 w-full min-h-[663px] max-w-full overflow-hidden max-lg:min-h-[1100px] max-md:py-3 max-md:min-h-[1000px] max-sm:min-h-[1200px] mb-4 md:mb-4 lg:mb-2"
      role="region"
      aria-labelledby="categories-heading"
    >
      <div className="flex relative flex-col items-start self-stretch max-w-full w-full">
        <div className="flex relative flex-col gap-4 items-center self-stretch pt-8 w-full">
          <header className="flex relative flex-col items-center w-full mb-6">
            <h2
              id="categories-heading"
              className="relative text-xl font-medium text-center text-neutral-700"
            >
              Categories
            </h2>
          </header>

          <div className="flex relative flex-col items-center px-4 lg:px-12 py-0 w-full max-w-full overflow-hidden">
            {loading && <p>Loading categories...</p>}
            {error && <p className="text-red-500">{error}</p>}
            {!loading && !error && categories.length > 0 && (
              <div className="grid relative gap-3 w-full max-w-[1375px]
                grid-cols-[2fr_1fr_1fr_2fr] grid-rows-[325px_277px] h-[613px]
                max-lg:gap-4 max-lg:h-auto max-lg:grid-cols-[1fr_1fr] max-lg:grid-rows-[350px_250px_200px_300px]
                max-md:grid-cols-[1fr_1fr] max-md:grid-rows-[400px_250px_200px_300px]
                max-sm:grid-cols-[1fr] max-sm:grid-rows-[350px_300px_250px_250px_350px] max-sm:h-auto">

                {categories.slice(0, 5).map((category, index) => (
                  <div
                    key={category.id}
                    className={`${responsiveClasses[index % responsiveClasses.length]} cursor-pointer focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 overflow-hidden rounded-lg`}
                    onMouseEnter={() => handleMouseEnter(category.id)}
                    onMouseLeave={handleMouseLeave}
                    onFocus={() => handleMouseEnter(category.id)}
                    onBlur={handleMouseLeave}
                    onKeyDown={(e) => handleKeyDown(e, category.id)}
                    onClick={() => handleCategoryClick(category)}
                    tabIndex={0}
                    role="button"
                    aria-label={`${category.name} category - Click to explore`}
                    aria-describedby={`${category.id}-description`}
                  >
                    <img
                      src={`${UPLOAD_URL}/category/${category.image}`}
                      alt={category.name}
                      className={`
                        absolute top-0 left-0 w-full h-full object-cover
                        ${index < 2 ? 'object-contain' : 'object-cover'}
                      `}
                    />

                    {/* Hover Overlay */}
                    <div
                      className={`absolute inset-0 bg-yellow-500 transition-opacity duration-300 ease-in-out ${
                        hoveredItem === category.id ? 'opacity-70' : 'opacity-0'
                      }`}
                      aria-hidden="true"
                    />

                    {/* Hover Text */}
                   {/* Hover Text - Desktop/Tablet */}
<div
  className={`absolute inset-0 flex flex-col items-center justify-center z-20 transition-opacity duration-300 ease-in-out hidden sm:flex ${
    hoveredItem === category.id ? 'opacity-100' : 'opacity-0'
  }`}
  aria-hidden={hoveredItem !== category.id}
  id={`${category.id}-description`}
>
  <h3 className="text-3xl font-bold text-white mb-2 max-lg:text-2xl max-md:text-2xl px-2 text-center">
    {category.name}
  </h3>
  <p className="text-lg text-white text-center max-lg:text-base max-md:text-base px-4">
    Explore our {category.name} collection
  </p>
</div>

{/* Mobile Text - Always visible at bottom center */}
<div className="absolute bottom-3 left-1/2 -translate-x-1/2 w-full text-center block sm:hidden z-20">
  <div className="bg-black/50 text-white px-3 py-1 rounded-md inline-block">
    <h3 className="text-base font-semibold">{category.name}</h3>
    
  </div>
</div>

                  </div>
                ))}

              </div>
            )}
          </div>
        </div>

        <div className="w-full h-2 md:h-2 lg:h-2 mt-4 md:mt-4 lg:mt-8"></div>
      </div>
    </section>
  );
};

export default CategoriesSection;
